import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Phone, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Thirugoo Logo Component
const ThirugooLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="/thirugoo-logo.png" 
    alt="Thirugoo" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const Login = () => {
  const navigate = useNavigate();
  const { sendOTP, verifyOTP } = useAuth();
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      await sendOTP(phone);
      toast.success('OTP sent! Use 121212 for testing');
      setStep('otp');
    } catch (error) {
      toast.error(error.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const result = await verifyOTP(phone, otp);
      if (result.is_new_user) {
        navigate('/register', { state: { phone } });
      } else {
        toast.success('Welcome back!');
        const user = result.user;
        if (user.role === 'admin') navigate('/admin');
        else if (user.role === 'driver') navigate('/driver');
        else navigate('/customer');
      }
    } catch (error) {
      toast.error(error.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ThirugooLogo className="h-12 w-12" />
            <span className="text-3xl font-bold text-gray-900">Thirugoo</span>
          </div>
          <p className="text-gray-600">Your trusted local cab service</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">
              {step === 'phone' ? 'Login / Register' : 'Enter OTP'}
            </CardTitle>
            <CardDescription>
              {step === 'phone' 
                ? 'Enter your mobile number to continue' 
                : `OTP sent to +91 ${phone}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {step === 'phone' ? (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter 10-digit number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 h-12 text-lg"
                      maxLength={10}
                    />
                  </div>
                  <p className="text-xs text-gray-500">We'll send you an OTP for verification</p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-lg"
                  disabled={loading || phone.length !== 10}
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending OTP...</>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-center block">Enter the 6-digit OTP</Label>
                  <div className="flex justify-center">
                    <InputOTP 
                      value={otp} 
                      onChange={setOtp}
                      maxLength={6}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
                        <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
                        <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
                        <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
                        <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
                        <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <p className="text-xs text-center text-gray-500">Hint: Use 121212</p>
                </div>
                <Button 
                  onClick={handleVerifyOTP}
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-lg"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verifying...</>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => { setStep('phone'); setOtp(''); }}
                  className="w-full"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Change Number
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-gray-500 text-sm">
          By continuing, you agree to our Terms of Service
        </p>

        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="w-full mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
        </Button>
      </div>
    </div>
  );
};

export default Login;
